import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Plug, Key, Webhook, LayoutGrid, ScrollText,
  Plus, Trash2, RefreshCw, Eye, EyeOff, Copy,
  CheckCircle2, XCircle, Clock, AlertTriangle,
  ChevronDown, ChevronUp, Activity, Zap, Globe,
  ToggleLeft, ToggleRight,
} from "lucide-react";
import { apiClient } from "../services/api";
import i18n from "../i18n";
import { toast } from "sonner";

const TABS = [
  { id: "dashboard", icon: Activity },
  { id: "api-keys", icon: Key },
  { id: "webhooks", icon: Webhook },
  { id: "marketplace", icon: LayoutGrid },
  { id: "logs", icon: ScrollText },
];

// ── API Keys Modal ────────────────────────────────────────────────────────────

function ApiKeyModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", scopes: [], expires_at: "" });
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("v1/integrations/scopes").then(r => setScopes(Array.isArray(r.data) ? r.data : []));
  }, []);

  const toggleScope = (s) =>
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter(x => x !== s) : [...f.scopes, s],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("integrationsHub.errors.nameRequired")); return; }
    setLoading(true);
    try {
      const payload = { name: form.name, scopes: form.scopes };
      if (form.expires_at) payload.expires_at = form.expires_at;
      const { data } = await apiClient.post("v1/integrations/api-keys", payload);
      onCreated(data);
    } catch {
      toast.error(t("integrationsHub.errors.createError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-lg shadow-2xl">
        <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between">
          <h2 className="font-semibold text-[var(--lm-text)]">{t("integrationsHub.apiKeyModal.title")}</h2>
          <button onClick={onClose} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("integrationsHub.apiKeyModal.name")}</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t("integrationsHub.apiKeyModal.namePlaceholder")}
              className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--lm-muted)] mb-2">{t("integrationsHub.apiKeyModal.scopes")}</label>
            <div className="grid grid-cols-2 gap-2">
              {scopes.map(s => (
                <label key={s.scope} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.scopes.includes(s.scope)}
                    onChange={() => toggleScope(s.scope)}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-[var(--lm-text)]">{s.description}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("integrationsHub.apiKeyModal.expiration")}</label>
            <input
              type="datetime-local"
              value={form.expires_at}
              onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] hover:text-[var(--lm-text)]">
              {t("integrationsHub.common.cancel")}
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
              {loading ? t("integrationsHub.apiKeyModal.creating") : t("integrationsHub.apiKeyModal.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── New Key Result Banner ────────────────────────────────────────────────────

function NewKeyBanner({ keyData, onClose }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const copy = () => { navigator.clipboard.writeText(keyData.raw_key); toast.success(t("integrationsHub.keyBanner.copiedToast")); };
  return (
    <div className="p-4 rounded-xl border border-yellow-500/40 bg-yellow-500/10 mb-4">
      <p className="text-sm font-medium text-yellow-300 mb-2">
        {t("integrationsHub.keyBanner.warning")}
      </p>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs font-mono bg-[var(--lm-bg)] border border-[var(--lm-border)] rounded px-3 py-2 text-green-400">
          {visible ? keyData.raw_key : "•".repeat(40)}
        </code>
        <button onClick={() => setVisible(v => !v)} className="p-2 rounded bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-muted)] hover:text-[var(--lm-text)]">
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
        <button onClick={copy} className="p-2 rounded bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-muted)] hover:text-[var(--lm-text)]">
          <Copy className="w-4 h-4" />
        </button>
      </div>
      <button onClick={onClose} className="mt-2 text-xs text-[var(--lm-muted)] hover:text-[var(--lm-text)]">
        {t("integrationsHub.keyBanner.done")}
      </button>
    </div>
  );
}

// ── Webhook Modal ─────────────────────────────────────────────────────────────

function WebhookModal({ webhook, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: webhook?.name || "",
    url: webhook?.url || "",
    events: webhook?.events || [],
    timeout_seconds: webhook?.timeout_seconds || 10,
    retry_count: webhook?.retry_count || 3,
    payload_format: webhook?.payload_format || "json",
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("v1/integrations/events").then(r => setEvents(Array.isArray(r.data) ? r.data : []));
  }, []);

  const toggleEvent = (e) =>
    setForm(f => ({
      ...f,
      events: f.events.includes(e) ? f.events.filter(x => x !== e) : [...f.events, e],
    }));

  const categories = [...new Set(events.map(e => e.category))];

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim() || !form.url.trim()) { toast.error(t("integrationsHub.errors.nameUrlRequired")); return; }
    if (!form.url.startsWith("https://")) { toast.error(t("integrationsHub.errors.httpsRequired")); return; }
    if (form.events.length === 0) { toast.error(t("integrationsHub.errors.selectEvent")); return; }
    setLoading(true);
    try {
      if (webhook) {
        const { data } = await apiClient.put(`v1/integrations/webhooks/${webhook.id}`, form);
        onSaved(data);
      } else {
        const { data } = await apiClient.post("v1/integrations/webhooks", form);
        onSaved(data);
      }
    } catch {
      toast.error(t("integrationsHub.errors.saveError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-[var(--lm-border)] flex items-center justify-between sticky top-0 bg-[var(--lm-s1)]">
          <h2 className="font-semibold text-[var(--lm-text)]">{webhook ? t("integrationsHub.webhookModal.editTitle") : t("integrationsHub.webhookModal.newTitle")}</h2>
          <button onClick={onClose} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("integrationsHub.webhookModal.name")}</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={t("integrationsHub.webhookModal.namePlaceholder")} className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("integrationsHub.webhookModal.url")}</label>
              <input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://hooks.slack.com/..." className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("integrationsHub.webhookModal.payloadFormat")}</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "json",  label: "JSON",   sub: t("integrationsHub.webhookModal.jsonSub") },
                  { id: "slack", label: "Slack",  sub: "Slack Incoming Webhook" },
                  { id: "teams", label: "Teams",  sub: "Teams MessageCard" },
                ].map((opt) => (
                  <button key={opt.id} type="button"
                    onClick={() => setForm(f => ({ ...f, payload_format: opt.id }))}
                    className={`text-left p-2 rounded-lg border text-xs transition-colors ${
                      form.payload_format === opt.id
                        ? "border-blue-600 bg-blue-600/10 text-blue-600"
                        : "border-[var(--lm-border)] bg-[var(--lm-bg)] text-[var(--lm-muted)] hover:text-[var(--lm-text)]"
                    }`}>
                    <p className="font-semibold">{opt.label}</p>
                    <p className="text-[10px] mt-0.5 opacity-70">{opt.sub}</p>
                  </button>
                ))}
              </div>
              {form.payload_format !== "json" && (
                <p className="text-[10px] text-[var(--lm-muted)] mt-1 italic">
                  {t("integrationsHub.webhookModal.payloadNote")}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("integrationsHub.webhookModal.timeout")}</label>
              <input type="number" min={1} max={30} value={form.timeout_seconds}
                onChange={e => setForm(f => ({ ...f, timeout_seconds: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--lm-muted)] mb-1">{t("integrationsHub.webhookModal.retries")}</label>
              <input type="number" min={0} max={5} value={form.retry_count}
                onChange={e => setForm(f => ({ ...f, retry_count: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-text)] text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--lm-muted)] mb-2">
              {t("integrationsHub.webhookModal.events", { count: form.events.length, suffix: form.events.length !== 1 ? "s" : "" })}
            </label>
            {categories.map(cat => (
              <div key={cat} className="mb-3">
                <p className="text-xs font-semibold text-[var(--lm-muted)] uppercase tracking-wider mb-1">{cat}</p>
                <div className="space-y-1">
                  {events.filter(e => e.category === cat).map(ev => (
                    <label key={ev.event} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.events.includes(ev.event)} onChange={() => toggleEvent(ev.event)} className="rounded" />
                      <span className="text-xs text-[var(--lm-text)]">{ev.description}</span>
                      <code className="text-xs text-[var(--lm-muted)] ml-auto">{ev.event}</code>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] hover:text-[var(--lm-text)]">
              {t("integrationsHub.common.cancel")}
            </button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50">
              {loading ? t("integrationsHub.webhookModal.saving") : webhook ? t("integrationsHub.webhookModal.update") : t("integrationsHub.webhookModal.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiClient.get("v1/integrations/dashboard").then(r => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats ? [
    { label: t("integrationsHub.dashboard.activeApiKeys"), value: `${stats.api_keys.active}/${stats.api_keys.total}`, icon: Key, color: "blue" },
    { label: t("integrationsHub.dashboard.activeWebhooks"), value: `${stats.webhooks.active}/${stats.webhooks.total}`, icon: Webhook, color: "purple" },
    { label: t("integrationsHub.dashboard.totalDeliveries"), value: stats.deliveries.total, icon: Zap, color: "green" },
    { label: t("integrationsHub.dashboard.failedDeliveries"), value: stats.deliveries.failed, icon: AlertTriangle, color: "red" },
  ] : [];

  const colorMap = { blue: "text-blue-400", purple: "text-purple-400", green: "text-green-400", red: "text-red-400" };
  const bgMap = { blue: "bg-blue-500/10", purple: "bg-purple-500/10", green: "bg-green-500/10", red: "bg-red-500/10" };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.label} className={`p-4 rounded-xl border border-[var(--lm-border)] ${bgMap[c.color]}`}>
            <c.icon className={`w-5 h-5 mb-2 ${colorMap[c.color]}`} />
            <p className="text-2xl font-bold text-[var(--lm-text)]">{c.value ?? "—"}</p>
            <p className="text-xs text-[var(--lm-muted)] mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="p-5 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)]">
        <h3 className="font-semibold text-[var(--lm-text)] mb-4">{t("integrationsHub.dashboard.quickStart")}</h3>
        <div className="space-y-3">
          {[Key, Webhook, LayoutGrid, ScrollText].map((icon, index) => ({
            step: String(index + 1),
            title: t(`integrationsHub.dashboard.steps.${index}.title`),
            desc: t(`integrationsHub.dashboard.steps.${index}.desc`),
            icon,
          })).map(item => (
            <div key={item.step} className="flex items-start gap-4 p-3 rounded-lg bg-[var(--lm-bg)]">
              <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-white">{item.step}</span>
              </div>
              <div>
                <p className="text-sm font-medium text-[var(--lm-text)]">{item.title}</p>
                <p className="text-xs text-[var(--lm-muted)]">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── API Keys Tab ──────────────────────────────────────────────────────────────

function ApiKeysTab() {
  const { t, i18n } = useTranslation();
  const [keys, setKeys] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newKey, setNewKey] = useState(null);

  const load = () => apiClient.get("v1/integrations/api-keys").then(r => setKeys(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleCreated = (data) => {
    setNewKey(data);
    setShowModal(false);
    load();
    toast.success(t("integrationsHub.apiKeys.createdToast"));
  };

  const revoke = async (id) => {
    await apiClient.post(`v1/integrations/api-keys/${id}/revoke`);
    toast.success(t("integrationsHub.apiKeys.revokedToast"));
    load();
  };

  const del = async (id) => {
    if (!window.confirm(t("integrationsHub.apiKeys.deleteConfirm"))) return;
    await apiClient.delete(`v1/integrations/api-keys/${id}`);
    toast.success(t("integrationsHub.apiKeys.deletedToast"));
    load();
  };

  return (
    <div className="space-y-4">
      {newKey && <NewKeyBanner keyData={newKey} onClose={() => setNewKey(null)} />}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--lm-muted)]">{t("integrationsHub.apiKeys.total", { count: keys.length, suffix: keys.length !== 1 ? "s" : "" })}</p>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("integrationsHub.apiKeys.new")}
        </button>
      </div>

      {keys.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <Key className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("integrationsHub.apiKeys.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map(k => (
            <div key={k.id} className={`p-4 rounded-xl border ${k.is_active ? "border-[var(--lm-border)] bg-[var(--lm-s1)]" : "border-[var(--lm-border)] bg-[var(--lm-bg)] opacity-60"}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[var(--lm-text)] text-sm">{k.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${k.is_active ? "bg-green-500/15 text-green-400" : "bg-gray-500/15 text-gray-400"}`}>
                      {k.is_active ? t("integrationsHub.common.active") : t("integrationsHub.apiKeys.revoked")}
                    </span>
                  </div>
                  <code className="text-xs text-[var(--lm-muted)] mt-1 block">{k.key_prefix}••••••••••••••••</code>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(k.scopes || []).map(s => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">{s}</span>
                    ))}
                    {k.scopes?.length === 0 && <span className="text-xs text-[var(--lm-muted)]">{t("integrationsHub.apiKeys.noScope")}</span>}
                  </div>
                  {k.expires_at && (
                    <p className="text-xs text-yellow-400 mt-1">{t("integrationsHub.apiKeys.expires", { date: new Date(k.expires_at).toLocaleDateString(i18n.language) })}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {k.is_active && (
                    <button onClick={() => revoke(k.id)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/20">
                      {t("integrationsHub.apiKeys.revoke")}
                    </button>
                  )}
                  <button onClick={() => del(k.id)}
                    className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <ApiKeyModal onClose={() => setShowModal(false)} onCreated={handleCreated} />}
    </div>
  );
}

// ── Webhooks Tab ──────────────────────────────────────────────────────────────

function WebhooksTab() {
  const { t } = useTranslation();
  const [hooks, setHooks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editHook, setEditHook] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [deliveries, setDeliveries] = useState({});

  const load = () => apiClient.get("v1/integrations/webhooks").then(r => setHooks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const loadDeliveries = async (hookId) => {
    if (deliveries[hookId]) return;
    const { data } = await apiClient.get(`v1/integrations/webhooks/${hookId}/deliveries`);
    setDeliveries(d => ({ ...d, [hookId]: data }));
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    loadDeliveries(id);
  };

  const del = async (id) => {
    if (!window.confirm(t("integrationsHub.webhooks.deleteConfirm"))) return;
    await apiClient.delete(`v1/integrations/webhooks/${id}`);
    toast.success(t("integrationsHub.webhooks.deletedToast"));
    load();
  };

  const toggle = async (hook) => {
    await apiClient.put(`v1/integrations/webhooks/${hook.id}`, { is_active: !hook.is_active });
    toast.success(hook.is_active ? t("integrationsHub.webhooks.disabledToast") : t("integrationsHub.webhooks.enabledToast"));
    load();
  };

  const handleSaved = () => {
    setShowModal(false);
    setEditHook(null);
    load();
    toast.success(t("integrationsHub.webhooks.savedToast"));
  };

  const StatusIcon = ({ status }) => {
    if (status === "success") return <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />;
    if (status === "failed") return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    return <Clock className="w-3.5 h-3.5 text-yellow-400" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--lm-muted)]">{t("integrationsHub.webhooks.total", { count: hooks.length, suffix: hooks.length !== 1 ? "s" : "" })}</p>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
          <Plus className="w-4 h-4" /> {t("integrationsHub.webhooks.new")}
        </button>
      </div>

      {hooks.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <Webhook className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("integrationsHub.webhooks.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {hooks.map(h => (
            <div key={h.id} className="rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[var(--lm-text)] text-sm">{h.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${h.is_active ? "bg-green-500/15 text-green-400" : "bg-gray-500/15 text-gray-400"}`}>
                        {h.is_active ? t("integrationsHub.common.active") : t("integrationsHub.common.inactive")}
                      </span>
                      {h.success_rate !== null && (
                        <span className="text-xs text-[var(--lm-muted)]">{t("integrationsHub.webhooks.successRate", { rate: h.success_rate })}</span>
                      )}
                    </div>
                    <p className="text-xs text-blue-400 mt-1 truncate">{h.url}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(h.events || []).slice(0, 4).map(e => (
                        <span key={e} className="text-xs px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">{e}</span>
                      ))}
                      {h.events?.length > 4 && (
                        <span className="text-xs text-[var(--lm-muted)]">+{h.events.length - 4}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => toggle(h)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">
                      {h.is_active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => { setEditHook(h); setShowModal(true); }}
                      className="text-xs px-2 py-1 rounded bg-[var(--lm-bg)] text-[var(--lm-muted)] hover:text-[var(--lm-text)] border border-[var(--lm-border)]">
                      {t("integrationsHub.common.edit")}
                    </button>
                    <button onClick={() => del(h.id)} className="p-1.5 rounded text-red-400 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => toggleExpand(h.id)} className="p-1.5 text-[var(--lm-muted)]">
                      {expandedId === h.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {expandedId === h.id && (
                <div className="border-t border-[var(--lm-border)] p-4 bg-[var(--lm-bg)]">
                  <p className="text-xs font-semibold text-[var(--lm-muted)] uppercase tracking-wider mb-2">{t("integrationsHub.webhooks.latestDeliveries")}</p>
                  {!deliveries[h.id] ? (
                    <p className="text-xs text-[var(--lm-muted)]">{t("integrationsHub.common.loading")}</p>
                  ) : deliveries[h.id].length === 0 ? (
                    <p className="text-xs text-[var(--lm-muted)]">{t("integrationsHub.webhooks.noDelivery")}</p>
                  ) : (
                    <div className="space-y-1">
                      {deliveries[h.id].slice(0, 5).map(d => (
                        <div key={d.id} className="flex items-center gap-3 text-xs">
                          <StatusIcon status={d.status} />
                          <code className="text-[var(--lm-muted)]">{d.event_type}</code>
                          {d.http_status && <span className={d.http_status < 300 ? "text-green-400" : "text-red-400"}>{d.http_status}</span>}
                          <span className="text-[var(--lm-muted)] ml-auto">{d.created_at ? new Date(d.created_at).toLocaleString(i18n.language) : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <WebhookModal
          webhook={editHook}
          onClose={() => { setShowModal(false); setEditHook(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ── Marketplace Tab ────────────────────────────────────────────────────────────

function MarketplaceTab() {
  const { t } = useTranslation();
  const [connectors, setConnectors] = useState([]);
  const [configuring, setConfiguring] = useState(null);

  useEffect(() => {
    apiClient.get("v1/integrations/marketplace").then(r => setConnectors(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const statusStyle = {
    available:    "bg-green-500/15 text-green-400 border-green-500/30",
    coming_soon:  "bg-gray-500/15 text-gray-400 border-gray-500/30",
    enterprise:   "bg-purple-500/15 text-purple-400 border-purple-500/30",
  };

  const statusLabel = {
    available:   t("integrationsHub.marketplace.available"),
    coming_soon: t("integrationsHub.marketplace.comingSoon"),
    enterprise:  t("integrationsHub.marketplace.enterprise"),
  };

  const categories = [...new Set(connectors.map(c => c.category))];

  return (
    <div className="space-y-6">
      {categories.map(cat => (
        <div key={cat}>
          <h3 className="text-xs font-semibold text-[var(--lm-muted)] uppercase tracking-wider mb-3">{cat}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connectors.filter(c => c.category === cat).map(c => (
              <div key={c.id} className="p-4 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--lm-bg)] border border-[var(--lm-border)] flex items-center justify-center">
                      <Globe className="w-5 h-5 text-[var(--lm-muted)]" />
                    </div>
                    <div>
                      <p className="font-medium text-[var(--lm-text)] text-sm">{c.name}</p>
                      <p className="text-xs text-[var(--lm-muted)]">{c.category}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle[c.status]}`}>
                    {statusLabel[c.status]}
                  </span>
                </div>
                <p className="text-xs text-[var(--lm-muted)] leading-relaxed">{c.description}</p>
                {c.status === "available" && (
                  <button
                    onClick={() => setConfiguring(c)}
                    className="w-full text-xs py-2 rounded-lg bg-blue-600/10 text-blue-400 hover:bg-blue-600/20 border border-blue-500/20 font-medium">
                    {t("integrationsHub.marketplace.configure")}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {configuring && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[var(--lm-s1)] border border-[var(--lm-border)] rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-[var(--lm-text)]">{t("integrationsHub.marketplace.configureTitle", { name: configuring.name })}</h2>
              <button onClick={() => setConfiguring(null)} className="text-[var(--lm-muted)] hover:text-[var(--lm-text)]">✕</button>
            </div>
            <p className="text-sm text-[var(--lm-muted)] mb-4">
              {t("integrationsHub.marketplace.configUnavailable", { name: configuring.name })}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfiguring(null)}
                className="flex-1 py-2 text-sm rounded-lg bg-[var(--lm-bg)] text-[var(--lm-muted)] hover:text-[var(--lm-text)] border border-[var(--lm-border)]">
                {t("integrationsHub.common.close")}
              </button>
              <button onClick={() => setConfiguring(null)}
                className="flex-1 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium">
                {t("integrationsHub.marketplace.viewDocs")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────────

function LogsTab() {
  const { t } = useTranslation();
  const [deliveries, setDeliveries] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => apiClient.get("v1/integrations/deliveries?limit=100").then(r => setDeliveries(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const retry = async (id) => {
    await apiClient.post(`v1/integrations/deliveries/${id}/retry`);
    toast.success(t("integrationsHub.logs.retryToast"));
    load();
  };

  const processBatch = async () => {
    const { data } = await apiClient.post("v1/integrations/deliveries/process-retries");
    if (data?.processed > 0) {
      toast.success(t("integrationsHub.logs.processedToast", { processed: data.processed, success: data.success, failed: data.failed }));
    } else {
      toast.info(t("integrationsHub.logs.noRetryToast"));
    }
    load();
  };

  const filtered = filter === "all" ? deliveries : deliveries.filter(d => d.status === filter);

  const StatusIcon = ({ status }) => {
    if (status === "success") return <CheckCircle2 className="w-4 h-4 text-green-400" />;
    if (status === "failed") return <XCircle className="w-4 h-4 text-red-400" />;
    return <Clock className="w-4 h-4 text-yellow-400" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          {["all", "success", "failed", "pending"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs rounded-lg border ${filter === f ? "bg-blue-600 text-white border-blue-600" : "bg-[var(--lm-bg)] text-[var(--lm-muted)] border-[var(--lm-border)] hover:text-[var(--lm-text)]"}`}>
              {f === "all" ? t("integrationsHub.common.all") : t(`integrationsHub.common.${f}`)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={processBatch}
            className="px-3 py-1.5 text-xs rounded-lg border border-blue-600 bg-blue-600/10 text-blue-600 hover:bg-blue-600/20 flex items-center gap-1.5"
            title={t("integrationsHub.logs.retryAllTitle")}>
            <RefreshCw className="w-3.5 h-3.5" />
            {t("integrationsHub.logs.processRetries")}
          </button>
          <button onClick={load} className="p-2 rounded-lg bg-[var(--lm-bg)] border border-[var(--lm-border)] text-[var(--lm-muted)] hover:text-[var(--lm-text)]">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[var(--lm-muted)]">
          <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>{t("integrationsHub.logs.empty")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => (
            <div key={d.id} className="p-4 rounded-xl border border-[var(--lm-border)] bg-[var(--lm-s1)] flex items-start gap-4">
              <StatusIcon status={d.status} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-xs text-[var(--lm-text)] font-medium">{d.event_type}</code>
                  {d.webhook_name && <span className="text-xs text-[var(--lm-muted)]">→ {d.webhook_name}</span>}
                  {d.http_status && (
                    <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${d.http_status < 300 ? "text-green-400 bg-green-500/10" : "text-red-400 bg-red-500/10"}`}>
                      {d.http_status}
                    </span>
                  )}
                  <span className="text-xs text-[var(--lm-muted)] ml-auto">
                    {d.created_at ? new Date(d.created_at).toLocaleString(i18n.language) : ""}
                  </span>
                </div>
                {d.status === "failed" && d.response_body && (
                  <p className="text-xs text-red-400 mt-1 truncate">{d.response_body}</p>
                )}
              </div>
              {d.status === "failed" && (
                <button onClick={() => retry(d.id)}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/20 flex-shrink-0">
                  <RefreshCw className="w-3 h-3" /> {t("integrationsHub.logs.retry")}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Hub ───────────────────────────────────────────────────────────────────────

export default function IntegrationsHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams({ tab: id });

  const renderTab = () => {
    switch (activeTab) {
      case "dashboard":   return <DashboardTab />;
      case "api-keys":    return <ApiKeysTab />;
      case "webhooks":    return <WebhooksTab />;
      case "marketplace": return <MarketplaceTab />;
      case "logs":        return <LogsTab />;
      default:            return <DashboardTab />;
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20">
          <Plug className="w-6 h-6 text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[var(--lm-text)]">{t("integrationsHub.title")}</h1>
          <p className="text-sm text-[var(--lm-muted)]">{t("integrationsHub.subtitle")}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[var(--lm-border)] overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? "border-blue-500 text-blue-400"
                : "border-transparent text-[var(--lm-muted)] hover:text-[var(--lm-text)]"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {t(`integrationsHub.tabs.${tab.id}`)}
          </button>
        ))}
      </div>

      {renderTab()}
    </div>
  );
}
